import "./login.scss";
import "bootstrap/dist/css/bootstrap.min.css";
import { useContext, useState, useRef, useEffect } from "react";
import localforage from "localforage";
import { useNavigate } from "react-router-dom";
import { BusinessService } from "../../services";
import { UserContext } from "../../context/User.context";
import { BrandContext } from "../../context/Brand.context";
import { CircularProgress } from "@mui/material";

export default function Login() {
  const [step, setStep] = useState("identifier"); // "identifier" | "otp"
  const [load, setLoad] = useState(false);
  const [error, setError] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [email, setEmail] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);

  const user = useContext(UserContext);
  const brand = useContext(BrandContext);
  const history = useNavigate();
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Auto-submit OTP when all 6 digits entered
  useEffect(() => {
    const code = otp.join("");
    if (code.length === 6) {
      handleVerifyOTP(code);
    }
  }, [otp]);

  // Step 1: Send OTP
  async function handleSendOTP() {
    setLoad(true);
    setError("");

    // OTP flow — works for both email and idKey
    try {
      const result = await BusinessService.BCSendOTP({
        payload: { identifier },
        toaster: false,
      });

      const data = result.response || result.error;

      if (data && data.success) {
        setEmail(data.data.emailFull);
        setMaskedEmail(data.data.email);
        setStep("otp");
        setResendTimer(60);
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs[0]?.current?.focus(), 100);
      } else {
        setError(data?.message || "User not found");
      }
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }

    setLoad(false);
  }

  // Step 2: Verify OTP
  async function handleVerifyOTP(code) {
    if (load) return;
    setLoad(true);
    setError("");

    try {
      const result = await BusinessService.BCVerifyOTP({
        payload: { email, otp: code },
        toaster: false,
      });

      const data = result.response || result.error;

      if (data && data.success) {
        await localforage.setItem("brands", data.data.brands);
        await localforage.setItem("token", data.data.token);
        user.updateUser({ ...data.data.user, brands: data.data.brands });

        if (data.data.brands && data.data.brands.length === 1) {
          history("/scanner");
        } else {
          history("/brand");
        }
      } else {
        setError(data?.message || "Invalid or expired code");
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs[0]?.current?.focus(), 100);
      }
    } catch (e) {
      setError("Invalid or expired code");
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs[0]?.current?.focus(), 100);
    }

    setLoad(false);
  }

  // OTP input handlers
  function handleOtpChange(index, value) {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, "").split("").slice(0, 6);
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs[nextIndex]?.current?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs[index + 1]?.current?.focus();
    }
  }

  function handleOtpKeyDown(index, e) {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
  }

  async function handleResendOTP() {
    if (resendTimer > 0) return;
    setLoad(true);
    setError("");

    try {
      const result = await BusinessService.BCSendOTP({
        payload: { identifier },
        toaster: false,
      });

      const data = result.response || result.error;

      if (data && data.success) {
        setResendTimer(60);
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs[0]?.current?.focus(), 100);
      }
    } catch (e) {}

    setLoad(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter") handleSendOTP();
  }

  return (
    <div className="middle" style={{ height: "90vh" }}>
      <div
        className="mobOne"
        style={{
          gap: "151px",
          padding: "121px 73px 121px 73px",
          display: "flex",
          borderRadius: "27px",
          background: "#FFF",
          boxShadow: "0px 14px 24px 0px rgba(0, 0, 0, 0.05)",
        }}
      >
        {step === "identifier" ? (
          <>
            <div className="w-100">
              <div
                style={{
                  marginBottom: "40px",
                  color: "#000",
                  fontFamily: "Inter",
                  fontSize: "36px",
                  fontWeight: "700",
                  lineHeight: "32px",
                  letterSpacing: "0.1px",
                }}
              >
                Login
              </div>
              <div
                style={{
                  color: "#6C757D",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontWeight: "400",
                  lineHeight: "26px",
                }}
              >
                Enter your email address or user ID to continue.
              </div>
            </div>
            <div className="w-100">
              <div
                style={{
                  marginBottom: "14px",
                  color: "#141414",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontWeight: "700",
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                }}
              >
                Email or User ID
              </div>
              <input
                type="text"
                name="identifier"
                onChange={(e) => {
                  setIdentifier(e.target.value);
                  setError("");
                }}
                value={identifier}
                className="form-control text-field"
                placeholder="Enter your email or user ID"
                onKeyDown={handleKeyDown}
                autoFocus
              />
              {error && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "14px",
                    marginTop: "8px",
                    fontFamily: "Inter",
                  }}
                >
                  {error}
                </div>
              )}
              <button
                className="btn btn-primary button d-flex center mt-18"
                disabled={!identifier.trim() || load}
                onClick={handleSendOTP}
              >
                {load && <CircularProgress size={14} color={"inherit"} />}
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="w-100">
              <div
                style={{
                  marginBottom: "16px",
                  color: "#000",
                  fontFamily: "Inter",
                  fontSize: "36px",
                  fontWeight: "700",
                  lineHeight: "32px",
                  letterSpacing: "0.1px",
                }}
              >
                Verify Code
              </div>
              <div
                style={{
                  color: "#6C757D",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontWeight: "400",
                  lineHeight: "26px",
                }}
              >
                We sent a verification code to{" "}
                <span style={{ fontWeight: "600", color: "#141414" }}>
                  {maskedEmail}
                </span>
              </div>
            </div>
            <div className="w-100">
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                  marginBottom: "24px",
                }}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={otpRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
                      handleOtpChange(index, pasted);
                    }}
                    style={{
                      width: "52px",
                      height: "52px",
                      textAlign: "center",
                      fontSize: "24px",
                      fontWeight: "600",
                      fontFamily: "Inter",
                      border: "2px solid #E5E7EB",
                      borderRadius: "12px",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#000")}
                    onBlur={(e) => (e.target.style.borderColor = "#E5E7EB")}
                  />
                ))}
              </div>

              {error && (
                <div
                  style={{
                    color: "#dc3545",
                    fontSize: "14px",
                    marginBottom: "16px",
                    textAlign: "center",
                    fontFamily: "Inter",
                  }}
                >
                  {error}
                </div>
              )}

              {load && (
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  <CircularProgress size={24} />
                </div>
              )}

              <div
                style={{
                  textAlign: "center",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  color: "#6C757D",
                }}
              >
                {resendTimer > 0 ? (
                  <span>Resend code in {resendTimer}s</span>
                ) : (
                  <span
                    onClick={handleResendOTP}
                    style={{
                      color: "#000",
                      fontWeight: "600",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Resend Code
                  </span>
                )}
              </div>

              <div
                onClick={() => {
                  setStep("identifier");
                  setError("");
                  setOtp(["", "", "", "", "", ""]);
                }}
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  color: "#000",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Back to Login
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
